import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePlayer } from '@/hooks/usePlayers';
import { ApiError, apiPost } from '@/lib/api';
import { EpProfileSchema } from '../../shared/elite-prospects';

export function EliteProspectsPanel({ playerId }: { playerId: string }) {
  const { data } = usePlayer(playerId);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  if (!data) return null;
  const epId = data.player.epId;
  const record = data.sourceRecords.find(
    (s) =>
      s.provider === 'Elite Prospects' &&
      s.sourceUrl === `https://www.eliteprospects.com/player/${epId}`
  );
  const parsed = (() => {
    try {
      return EpProfileSchema.safeParse(JSON.parse(record?.detailsJson ?? '{}').profile);
    } catch {
      return null;
    }
  })();
  const profile = parsed?.success ? parsed.data : null;
  const season = profile?.latestStats;
  const stats = season?.regularStats;
  async function refresh() {
    setBusy(true);
    setMessage(null);
    try {
      await apiPost(`/api/players/${playerId}/elite-prospects/refresh`, {});
      await qc.invalidateQueries({ queryKey: ['player', playerId] });
      setMessage('Elite Prospects profile checked.');
    } catch (error) {
      const body = error instanceof ApiError ? (error.body as { error?: string }) : null;
      setMessage(body?.error ?? 'Unable to refresh Elite Prospects.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="surface-soft space-y-3 rounded-control p-4"
      aria-label="Elite Prospects profile"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold">Elite Prospects</h2>
        {epId && (
          <a
            href={`https://www.eliteprospects.com/player/${epId}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline"
          >
            View source profile
          </a>
        )}
      </div>
      {!data.eliteProspectsConfigured && (
        <p className="text-sm text-muted">
          Automated enrichment is awaiting API access. Linked source profiles remain available.
        </p>
      )}
      {!epId && (
        <p className="text-sm text-muted">
          Add the verified Elite Prospects ID under External IDs to link this player.
        </p>
      )}
      {profile && (
        <>
          <p className="text-sm">
            {[
              profile.position,
              profile.nationality?.name,
              profile.placeOfBirth,
              profile.height?.metrics ? `${profile.height.metrics} cm` : null,
              profile.shoots ? `Shoots ${profile.shoots}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {season && (
            <div className="text-sm">
              <p className="font-medium">
                Latest published season: {season.season.slug} ·{' '}
                {season.teamName ?? season.team?.name ?? 'Team unavailable'} ·{' '}
                {season.leagueName ?? season.league?.name ?? 'League unavailable'}
              </p>
              {stats && (
                <p>
                  {Object.entries(stats)
                    .filter(([, value]) => value != null)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(' · ')}
                </p>
              )}
              {season.status && season.status !== 'rostered' && (
                <p className="text-amber-700">EP roster status: {season.status}</p>
              )}
            </div>
          )}
          <p className="text-xs text-muted">
            Source: Elite Prospects · Checked{' '}
            {new Date(record!.observedAt * 1000).toLocaleDateString()}. Published season data may
            differ from the current roster assignment.
          </p>
        </>
      )}
      {data.eliteProspectsConfigured && epId && (
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void refresh()}
          disabled={busy}
        >
          {busy ? 'Checking…' : 'Refresh Elite Prospects'}
        </button>
      )}
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
    </section>
  );
}
